import {
    createSlice, createAsyncThunk, createEntityAdapter, createSelector,
    PayloadAction, EntityState
} from '@reduxjs/toolkit';
import { apiFetch } from '../../../lib/api';
import type {
    Ticket, TicketComment, TicketListParams, TicketStatus
} from './types';
import type { RootState } from '../../index';

/* ---------------- entity adapters ---------------- */
const ticketsAdapter = createEntityAdapter<Ticket>();
const commentsAdapter = createEntityAdapter<TicketComment>({
    sortComparer: (a, b) => {
        const ax = (a.createdAt?.seconds ?? a.createdAt?._seconds ?? 0);
        const bx = (b.createdAt?.seconds ?? b.createdAt?._seconds ?? 0);
        return ax - bx;
    }
});
const EMPTY_COMMENT_STATE = commentsAdapter.getInitialState();
const EMPTY_COMMENT_BUCKET_META = {
    loading: false,
    error: null as string | null,
    fetchedAt: null as number | null,
};
const EMPTY_ASSIGNEE_OPTIONS: TicketAssigneeOptionRecord[] = [];
const EMPTY_USER_IDENTITY_MAP: Record<string, TicketUserIdentityRecord | undefined> = {};

export const TICKET_DETAIL_TTL_MS = 60_000;
export const TICKET_CONTEXT_TTL_MS = 60_000;
export const TICKET_COMMENTS_TTL_MS = 60_000;
export const TICKET_ASSIGNEE_LIST_TTL_MS = 5 * 60_000;
export const TICKET_USER_IDENTITY_TTL_MS = 5 * 60_000;

/* ---------------- state ---------------- */
interface TicketCommentsBucket {
    data: EntityState<TicketComment, string>;
    loading: boolean;
    error: string | null;
    fetchedAt: number | null;
    // runtime only in listener middleware (we won't store unsubs here)
}

interface TicketDetailMeta {
    loading: boolean;
    error: string | null;
    fetchedAt: number | null;
}

interface TicketContextMeta {
    loading: boolean;
    error: string | null;
    fetchedAt: number | null;
}

export interface TicketAssigneeOptionRecord {
    id: string;
    name: string;
    email: string;
    title?: string | null;
    role?: string | null;
    photoURL?: string | null;
}

interface TicketUserIdentityRecord {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    displayName?: string | null;
    name?: string | null;
    email?: string | null;
    photoURL?: string | null;
    username?: string | null;
    businessName?: string | null;
    businessLogoURL?: string | null;
}

interface TicketsState {
    tickets: EntityState<Ticket, string>;
    ticketDetailsById: Record<string, Ticket | undefined>;
    ticketDetailMetaById: Record<string, TicketDetailMeta | undefined>;
    ticketContextByKey: Record<string, any | undefined>;
    ticketContextMetaByKey: Record<string, TicketContextMeta | undefined>;
    orgAssigneesByKey: Record<string, TicketAssigneeOptionRecord[] | undefined>;
    orgAssigneeMetaByKey: Record<string, TicketContextMeta | undefined>;
    internalTeamAssigneesByTeamId: Record<string, TicketAssigneeOptionRecord[] | undefined>;
    internalTeamAssigneeMetaByTeamId: Record<string, TicketContextMeta | undefined>;
    userIdentityById: Record<string, TicketUserIdentityRecord | undefined>;
    userIdentityMetaById: Record<string, TicketDetailMeta | undefined>;
    listLoading: boolean;
    listError: string | null;
    lastParams: TicketListParams | null;

    creating: boolean;
    createError: string | null;

    mutating: boolean;
    mutationError: string | null;

    commentsByTicket: Record<string, TicketCommentsBucket>;
    // live tracking for UI (ids currently being live-synced)
    liveTicketIds: string[];
}

const initialState: TicketsState = {
    tickets: ticketsAdapter.getInitialState(),
    ticketDetailsById: {},
    ticketDetailMetaById: {},
    ticketContextByKey: {},
    ticketContextMetaByKey: {},
    orgAssigneesByKey: {},
    orgAssigneeMetaByKey: {},
    internalTeamAssigneesByTeamId: {},
    internalTeamAssigneeMetaByTeamId: {},
    userIdentityById: {},
    userIdentityMetaById: {},
    listLoading: false,
    listError: null,
    lastParams: null,

    creating: false,
    createError: null,

    mutating: false,
    mutationError: null,

    commentsByTicket: {},
    liveTicketIds: [],
};

/* ---------------- thunks (REST) ---------------- */


// ticketsSlice.ts — near thunks
export const fetchGlobalQueue = createAsyncThunk<Ticket[], void>(
  'tickets/fetchGlobalQueue',
  async () => {
    // The backend will scope by role and teamIds from the session token.
    return await apiFetch<Ticket[]>('/api/tickets?unassigned=true&orderBy=plannedEndAt&direction=asc&limit=200');
  }
);

export const fetchMyQueue = createAsyncThunk<Ticket[], void>(
  'tickets/fetchMyQueue',
  async (_, { getState }) => {
    // @ts-ignore
    const { user } = (getState() as any).auth || {};
    const usp = new URLSearchParams({
      assignedAgentId: user?.uid,
      status: 'open,in_progress', // personal queue common default
      orderBy: 'plannedEndAt',
      direction: 'asc',
      limit: '200',
    });
    return await apiFetch<Ticket[]>(`/api/tickets?${usp.toString()}`);
  }
);

// Assign to self wrapper (uses new endpoint)
export const assignToSelf = createAsyncThunk<
  { ok: true; id: string },
  { id: string; preferredTeamId?: string | null; clientMutationId?: string | null }
>(
  'tickets/assignToSelf',
  async ({ id, preferredTeamId = null, clientMutationId = null }) => {
    await apiFetch(`/api/tickets/${id}/assign-self`, {
      method: 'POST',
      body: JSON.stringify({ preferredTeamId, clientMutationId }),
    });
    return { ok: true, id };
  }
);


// List (role-aware on backend)
export const fetchTickets = createAsyncThunk<Ticket[], TicketListParams | undefined>(
    'tickets/fetchTickets',
    async (params = {}) => {
        const q: any = { ...params };
        if (!q.orderBy) q.orderBy = 'plannedEndAt';
        if (!q.direction) q.direction = 'asc';
        if (!q.limit) q.limit = 200;
        const usp = new URLSearchParams();
        Object.entries(q).forEach(([k, v]) => {
            if (v !== undefined && v !== null && String(v).length) usp.append(k, String(v));
        });
        const url = `/api/tickets${usp.toString() ? `?${usp.toString()}` : ''}`;
        const res = await apiFetch<Ticket[]>(url);
        return res;
    }
);

export function buildTicketContextCacheKey(params: {
    clientId?: string | null;
    projectId?: string | null;
    orgId?: string | null;
    clientSubscriptionId?: string | null;
}): string {
    return JSON.stringify({
        clientId: params.clientId ?? null,
        projectId: params.projectId ?? null,
        orgId: params.orgId ?? null,
        clientSubscriptionId: params.clientSubscriptionId ?? null,
    });
}

export function buildTicketAssigneeCacheKey(orgId?: string | null): string {
    const normalizedOrgId = String(orgId ?? '').trim();
    return normalizedOrgId || '__default__';
}

function normalizeTicketAssigneeOptions(raw: unknown): TicketAssigneeOptionRecord[] {
    if (!Array.isArray(raw)) return [];
    return raw
        .map((entry: any) => {
            const id = String(entry?.uid ?? entry?.id ?? '').trim();
            if (!id) return null;
            const normalized: TicketAssigneeOptionRecord = {
                id,
                name: String(entry?.name ?? entry?.email ?? id),
                email: String(entry?.email ?? '').trim(),
                title: entry?.title ? String(entry.title) : undefined,
                role: entry?.role ? String(entry.role) : undefined,
                photoURL: entry?.photoURL ? String(entry.photoURL) : null,
            };
            return normalized;
        })
        .filter((entry): entry is TicketAssigneeOptionRecord => Boolean(entry));
}

function normalizeTicketUserIdentity(raw: any, id: string): TicketUserIdentityRecord {
    return {
        id,
        firstName: raw?.firstName ? String(raw.firstName) : null,
        lastName: raw?.lastName ? String(raw.lastName) : null,
        displayName: raw?.displayName ? String(raw.displayName) : null,
        name: raw?.name ? String(raw.name) : null,
        email: raw?.email ? String(raw.email) : null,
        photoURL: raw?.photoURL ? String(raw.photoURL) : raw?.photoUrl ? String(raw.photoUrl) : null,
        username: raw?.username ? String(raw.username) : null,
        businessName: raw?.businessName ? String(raw.businessName) : null,
        businessLogoURL: raw?.businessLogoURL ? String(raw.businessLogoURL) : null,
    };
}

export const fetchTicketDetail = createAsyncThunk<
    Ticket,
    { id: string; force?: boolean }
>(
    'tickets/fetchTicketDetail',
    async ({ id }) => {
        return await apiFetch<Ticket>(`/api/tickets/${id}`);
    },
    {
        condition: ({ id, force = false }, { getState }) => {
            if (!id) return false;
            if (force) return true;
            const state = getState() as RootState;
            const meta = state.tickets.ticketDetailMetaById[id];
            if (meta?.loading) return false;
            if (meta?.fetchedAt && Date.now() - meta.fetchedAt < TICKET_DETAIL_TTL_MS) {
                return false;
            }
            return true;
        },
    },
);

export const fetchTicketCreateContext = createAsyncThunk<
    { key: string; data: any },
    {
        clientId?: string | null;
        projectId?: string | null;
        orgId?: string | null;
        clientSubscriptionId?: string | null;
        force?: boolean;
    }
>(
    'tickets/fetchTicketCreateContext',
    async ({ force: _force, ...params }) => {
        const usp = new URLSearchParams();
        if (params.clientId) usp.set('clientId', params.clientId);
        if (params.projectId) usp.set('projectId', params.projectId);
        if (params.orgId) usp.set('orgId', params.orgId);
        if (params.clientSubscriptionId) {
            usp.set('clientSubscriptionId', params.clientSubscriptionId);
        }
        const key = buildTicketContextCacheKey(params);
        const data = await apiFetch<any>(`/api/tickets/create-context?${usp.toString()}`);
        return { key, data };
    },
    {
        condition: ({ force = false, ...params }, { getState }) => {
            const key = buildTicketContextCacheKey(params);
            if (!key) return false;
            if (force) return true;
            const state = getState() as RootState;
            const meta = state.tickets.ticketContextMetaByKey[key];
            if (meta?.loading) return false;
            if (meta?.fetchedAt && Date.now() - meta.fetchedAt < TICKET_CONTEXT_TTL_MS) {
                return false;
            }
            return true;
        },
    },
);

export const fetchTicketComments = createAsyncThunk<
    { ticketId: string; comments: TicketComment[] },
    { id: string; force?: boolean }
>(
    'tickets/fetchTicketComments',
    async ({ id }) => {
        const comments = await apiFetch<TicketComment[]>(`/api/tickets/${id}/comments`);
        return { ticketId: id, comments };
    },
    {
        condition: ({ id, force = false }, { getState }) => {
            if (!id) return false;
            if (force) return true;
            const state = getState() as RootState;
            const bucket = state.tickets.commentsByTicket[id];
            if (bucket?.loading) return false;
            if (bucket?.fetchedAt && Date.now() - bucket.fetchedAt < TICKET_COMMENTS_TTL_MS) {
                return false;
            }
            return true;
        },
    },
);

export const fetchTicketOrgAssignees = createAsyncThunk<
    { key: string; assignees: TicketAssigneeOptionRecord[] },
    { orgId?: string | null; force?: boolean }
>(
    'tickets/fetchTicketOrgAssignees',
    async ({ force: _force, orgId }) => {
        const params = new URLSearchParams();
        const normalizedOrgId = String(orgId ?? '').trim();
        if (normalizedOrgId) params.set('orgId', normalizedOrgId);
        const response = await apiFetch<{ assignees?: unknown[] }>(
            `/api/tickets/assignees${params.toString() ? `?${params.toString()}` : ''}`,
        );
        return {
            key: buildTicketAssigneeCacheKey(normalizedOrgId),
            assignees: normalizeTicketAssigneeOptions(response?.assignees),
        };
    },
    {
        condition: ({ orgId, force = false }, { getState }) => {
            const key = buildTicketAssigneeCacheKey(orgId);
            if (force) return true;
            const state = getState() as RootState;
            const meta = state.tickets.orgAssigneeMetaByKey[key];
            if (meta?.loading) return false;
            if (meta?.fetchedAt && Date.now() - meta.fetchedAt < TICKET_ASSIGNEE_LIST_TTL_MS) {
                return false;
            }
            return true;
        },
    },
);

export const fetchTicketInternalTeamAssignees = createAsyncThunk<
    { teamNodeId: string; assignees: TicketAssigneeOptionRecord[] },
    { teamNodeId: string; force?: boolean }
>(
    'tickets/fetchTicketInternalTeamAssignees',
    async ({ teamNodeId }) => {
        const normalizedTeamNodeId = String(teamNodeId ?? '').trim();
        const response = await apiFetch<{ assignees?: unknown[] }>(
            `/api/tickets/internal-assignees?teamNodeId=${encodeURIComponent(normalizedTeamNodeId)}`,
        );
        return {
            teamNodeId: normalizedTeamNodeId,
            assignees: normalizeTicketAssigneeOptions(response?.assignees),
        };
    },
    {
        condition: ({ teamNodeId, force = false }, { getState }) => {
            const normalizedTeamNodeId = String(teamNodeId ?? '').trim();
            if (!normalizedTeamNodeId) return false;
            if (force) return true;
            const state = getState() as RootState;
            const meta = state.tickets.internalTeamAssigneeMetaByTeamId[normalizedTeamNodeId];
            if (meta?.loading) return false;
            if (meta?.fetchedAt && Date.now() - meta.fetchedAt < TICKET_ASSIGNEE_LIST_TTL_MS) {
                return false;
            }
            return true;
        },
    },
);

export const fetchTicketUserIdentity = createAsyncThunk<
    { id: string; identity: TicketUserIdentityRecord },
    { id: string; force?: boolean }
>(
    'tickets/fetchTicketUserIdentity',
    async ({ id }) => {
        const normalizedId = String(id ?? '').trim();
        const response = await apiFetch<any>(`/api/admin/users/${encodeURIComponent(normalizedId)}`);
        return {
            id: normalizedId,
            identity: normalizeTicketUserIdentity(response, normalizedId),
        };
    },
    {
        condition: ({ id, force = false }, { getState }) => {
            const normalizedId = String(id ?? '').trim();
            if (!normalizedId) return false;
            if (force) return true;
            const state = getState() as RootState;
            const meta = state.tickets.userIdentityMetaById[normalizedId];
            if (meta?.loading) return false;
            if (meta?.fetchedAt && Date.now() - meta.fetchedAt < TICKET_USER_IDENTITY_TTL_MS) {
                return false;
            }
            return true;
        },
    },
);

// Create (client)
export const createTicket = createAsyncThunk<{ id: string }, {
    title: string; description: string; priority?: 'P0' | 'P1' | 'P2' | 'P3';
    clientId?: string | null;
    assignedAgentId?: string | null;
    assignedOrgMemberId?: string | null;
    owningTeamNodeId?: string | null;
    projectId?: string | null; companyId?: string | null;
    milestoneId?: string | null;
    clientSubscriptionId?: string | null;
    offeringId?: string | null;
    offeringSubcategoryId?: string | null;
    kbaseProcessId?: string | null;
    estimatedMinutes?: number | null;
    remainingEstimateMinutes?: number | null;
    targetCompletionMinutes?: number | null;
    blockedByTicketIds?: string[];
    handoffToTicketIds?: string[];
    externalLinks?: { label?: string; url: string }[];
    tags?: string[]; teamId?: string | null; slaPolicyId?: string | null;
    orgId?: string; // ← NEW: required by backend
    clientMutationId?: string | null;
    plannedStartAt?: string | null;
    plannedEndAt?: string | null;
    timezone?: string | null;
 }>(
    'tickets/createTicket',
    async (payload, { getState }) => {
         const s: any = getState();
    // Try payload first; fall back to any auth slice values if you keep orgId there
    const authOrgId: string | undefined =
      s?.auth?.orgId ?? s?.auth?.user?.orgId ?? undefined;
    const effectiveOrgId: string | undefined =
      payload.orgId ?? authOrgId ?? undefined;
    if (!effectiveOrgId) {
      // Surface a clear client error instead of a 400 from the server
      throw new Error('orgId missing in client payload');
    }
        return apiFetch<{ id: string }>('/api/tickets', {
            method: 'POST',
            body: JSON.stringify({ ...payload, orgId: effectiveOrgId })
        });
    }
);

// Assign/unassign
export const assignTicket = createAsyncThunk<
    { ok: true; id: string; agentId: string | null; teamId: string | null },
    {
      id: string;
      agentId?: string | null;
      teamId?: string | null;
      clientMutationId?: string | null;
    }
>('tickets/assign', async ({ id, agentId = null, teamId = null, clientMutationId = null }) => {
    await apiFetch(`/api/tickets/${id}/assign`, {
        method: 'POST',
        body: JSON.stringify({ agentId, teamId, clientMutationId })
    });
    return { ok: true, id, agentId, teamId };
});

// Status transition
// in ticketsSlice.ts
export const updateTicketStatus = createAsyncThunk<
  { ok: true; id: string; status: TicketStatus },
  { id: string; status: TicketStatus; clientMutationId?: string | null }
>('tickets/status', async ({ id, status, clientMutationId = null }) => {
  await apiFetch(`/api/tickets/${id}/transition`, {  // <-- use /transition
    method: 'POST',
    body: JSON.stringify({ status, clientMutationId })
  });
  return { ok: true, id, status };
});


// Add comment
export const addTicketComment = createAsyncThunk<
    { id: string; ticketId: string },
    { id: string; body: string; internal?: boolean; clientMutationId?: string | null }
>('tickets/comment', async ({ id, body, internal = false, clientMutationId = null }) => {
    const res = await apiFetch<{ id: string }>(`/api/tickets/${id}/comments`, {
        method: 'POST',
        body: JSON.stringify({ body, internal, clientMutationId })
    });
    return { id: res.id, ticketId: id };
});

/* ---------------- slice ---------------- */

const slice = createSlice({
    name: 'tickets',
    initialState,
    reducers: {
        // Real-time snapshots upsert ticket
        ticketUpserted(state, action: PayloadAction<Ticket>) {
            ticketsAdapter.upsertOne(state.tickets, action.payload);
            const existingDetail = state.ticketDetailsById[action.payload.id];
            if (existingDetail) {
                state.ticketDetailsById[action.payload.id] = {
                    ...existingDetail,
                    ...action.payload,
                };
            }
        },
        ticketPatched(state, action: PayloadAction<{ id: string; changes: Partial<Ticket> }>) {
            const { id, changes } = action.payload;
            ticketsAdapter.updateOne(state.tickets, { id, changes });
            const existingDetail = state.ticketDetailsById[id];
            if (existingDetail) {
                state.ticketDetailsById[id] = {
                    ...existingDetail,
                    ...changes,
                };
            }
        },
        ticketReplaced(state, action: PayloadAction<{ tempId: string; ticket: Ticket }>) {
            const { tempId, ticket } = action.payload;
            if (tempId) {
                ticketsAdapter.removeOne(state.tickets, tempId);
                delete state.ticketDetailsById[tempId];
                delete state.ticketDetailMetaById[tempId];
            }
            ticketsAdapter.upsertOne(state.tickets, ticket);
            const existingDetail = state.ticketDetailsById[ticket.id];
            if (existingDetail) {
                state.ticketDetailsById[ticket.id] = {
                    ...existingDetail,
                    ...ticket,
                };
            }
        },
        ticketRemoved(state, action: PayloadAction<string>) {
            ticketsAdapter.removeOne(state.tickets, action.payload);
            delete state.ticketDetailsById[action.payload];
            delete state.ticketDetailMetaById[action.payload];
            delete state.commentsByTicket[action.payload];
        },
        // Real-time snapshots replace comments
        commentsSnapshotReplaced(state, action: PayloadAction<{ ticketId: string; comments: TicketComment[] }>) {
            const { ticketId, comments } = action.payload;
            if (!state.commentsByTicket[ticketId]) {
                state.commentsByTicket[ticketId] = {
                    data: commentsAdapter.getInitialState(),
                    loading: false,
                    error: null,
                    fetchedAt: null,
                };
            }
            commentsAdapter.setAll(state.commentsByTicket[ticketId].data, comments);
            state.commentsByTicket[ticketId].loading = false;
            state.commentsByTicket[ticketId].error = null;
            state.commentsByTicket[ticketId].fetchedAt = Date.now();
        },
        // UI: mark a ticket as live-subscribed
        markLiveSubscribed(state, action: PayloadAction<string>) {
            const id = action.payload;
            if (!state.liveTicketIds.includes(id)) state.liveTicketIds.push(id);
        },
        // UI: mark a ticket live-unsubscribed
        markLiveUnsubscribed(state, action: PayloadAction<string>) {
            state.liveTicketIds = state.liveTicketIds.filter(x => x !== action.payload);
        },
    },
    extraReducers: (b) => {
        // list
        b.addCase(fetchTickets.pending, (s, a) => {
            s.listLoading = true; s.listError = null; s.lastParams = (a.meta.arg ?? null);
        });
        b.addCase(fetchTickets.fulfilled, (s, a) => {
            s.listLoading = false;
            ticketsAdapter.setAll(s.tickets, a.payload);
        });
        b.addCase(fetchTickets.rejected, (s, a) => {
            s.listLoading = false; s.listError = a.error.message || 'Failed to load tickets';
        });

        b.addCase(fetchTicketDetail.pending, (s, a) => {
            const id = a.meta.arg.id;
            const prev = s.ticketDetailMetaById[id];
            s.ticketDetailMetaById[id] = {
                loading: true,
                error: null,
                fetchedAt: prev?.fetchedAt ?? null,
            };
        });
        b.addCase(fetchTicketDetail.fulfilled, (s, a) => {
            const id = a.meta.arg.id;
            ticketsAdapter.upsertOne(s.tickets, a.payload);
            s.ticketDetailsById[id] = {
                ...(s.ticketDetailsById[id] ?? {}),
                ...a.payload,
            };
            s.ticketDetailMetaById[id] = {
                loading: false,
                error: null,
                fetchedAt: Date.now(),
            };
        });
        b.addCase(fetchTicketDetail.rejected, (s, a) => {
            const id = a.meta.arg.id;
            const prev = s.ticketDetailMetaById[id];
            s.ticketDetailMetaById[id] = {
                loading: false,
                error: a.error.message || 'Failed to load ticket detail',
                fetchedAt: prev?.fetchedAt ?? null,
            };
        });

        b.addCase(fetchTicketCreateContext.pending, (s, a) => {
            const key = buildTicketContextCacheKey(a.meta.arg);
            const prev = s.ticketContextMetaByKey[key];
            s.ticketContextMetaByKey[key] = {
                loading: true,
                error: null,
                fetchedAt: prev?.fetchedAt ?? null,
            };
        });
        b.addCase(fetchTicketCreateContext.fulfilled, (s, a) => {
            s.ticketContextByKey[a.payload.key] = a.payload.data;
            s.ticketContextMetaByKey[a.payload.key] = {
                loading: false,
                error: null,
                fetchedAt: Date.now(),
            };
        });
        b.addCase(fetchTicketCreateContext.rejected, (s, a) => {
            const key = buildTicketContextCacheKey(a.meta.arg);
            const prev = s.ticketContextMetaByKey[key];
            s.ticketContextMetaByKey[key] = {
                loading: false,
                error: a.error.message || 'Failed to load ticket context',
                fetchedAt: prev?.fetchedAt ?? null,
            };
        });

        b.addCase(fetchTicketComments.pending, (s, a) => {
            const ticketId = a.meta.arg.id;
            if (!s.commentsByTicket[ticketId]) {
                s.commentsByTicket[ticketId] = {
                    data: commentsAdapter.getInitialState(),
                    loading: true,
                    error: null,
                    fetchedAt: null,
                };
                return;
            }
            s.commentsByTicket[ticketId].loading = true;
            s.commentsByTicket[ticketId].error = null;
        });
        b.addCase(fetchTicketComments.fulfilled, (s, a) => {
            const { ticketId, comments } = a.payload;
            if (!s.commentsByTicket[ticketId]) {
                s.commentsByTicket[ticketId] = {
                    data: commentsAdapter.getInitialState(),
                    loading: false,
                    error: null,
                    fetchedAt: Date.now(),
                };
            }
            commentsAdapter.setAll(s.commentsByTicket[ticketId].data, comments);
            s.commentsByTicket[ticketId].loading = false;
            s.commentsByTicket[ticketId].error = null;
            s.commentsByTicket[ticketId].fetchedAt = Date.now();
        });
        b.addCase(fetchTicketComments.rejected, (s, a) => {
            const ticketId = a.meta.arg.id;
            if (!s.commentsByTicket[ticketId]) {
                s.commentsByTicket[ticketId] = {
                    data: commentsAdapter.getInitialState(),
                    loading: false,
                    error: a.error.message || 'Failed to load comments',
                    fetchedAt: null,
                };
                return;
            }
            s.commentsByTicket[ticketId].loading = false;
            s.commentsByTicket[ticketId].error = a.error.message || 'Failed to load comments';
        });

        b.addCase(fetchTicketOrgAssignees.pending, (s, a) => {
            const key = buildTicketAssigneeCacheKey(a.meta.arg.orgId);
            const prev = s.orgAssigneeMetaByKey[key];
            s.orgAssigneeMetaByKey[key] = {
                loading: true,
                error: null,
                fetchedAt: prev?.fetchedAt ?? null,
            };
        });
        b.addCase(fetchTicketOrgAssignees.fulfilled, (s, a) => {
            s.orgAssigneesByKey[a.payload.key] = a.payload.assignees;
            s.orgAssigneeMetaByKey[a.payload.key] = {
                loading: false,
                error: null,
                fetchedAt: Date.now(),
            };
        });
        b.addCase(fetchTicketOrgAssignees.rejected, (s, a) => {
            const key = buildTicketAssigneeCacheKey(a.meta.arg.orgId);
            const prev = s.orgAssigneeMetaByKey[key];
            s.orgAssigneeMetaByKey[key] = {
                loading: false,
                error: a.error.message || 'Failed to load organization assignees',
                fetchedAt: prev?.fetchedAt ?? null,
            };
        });

        b.addCase(fetchTicketInternalTeamAssignees.pending, (s, a) => {
            const teamNodeId = String(a.meta.arg.teamNodeId ?? '').trim();
            const prev = s.internalTeamAssigneeMetaByTeamId[teamNodeId];
            s.internalTeamAssigneeMetaByTeamId[teamNodeId] = {
                loading: true,
                error: null,
                fetchedAt: prev?.fetchedAt ?? null,
            };
        });
        b.addCase(fetchTicketInternalTeamAssignees.fulfilled, (s, a) => {
            s.internalTeamAssigneesByTeamId[a.payload.teamNodeId] = a.payload.assignees;
            s.internalTeamAssigneeMetaByTeamId[a.payload.teamNodeId] = {
                loading: false,
                error: null,
                fetchedAt: Date.now(),
            };
        });
        b.addCase(fetchTicketInternalTeamAssignees.rejected, (s, a) => {
            const teamNodeId = String(a.meta.arg.teamNodeId ?? '').trim();
            const prev = s.internalTeamAssigneeMetaByTeamId[teamNodeId];
            s.internalTeamAssigneeMetaByTeamId[teamNodeId] = {
                loading: false,
                error: a.error.message || 'Failed to load internal team assignees',
                fetchedAt: prev?.fetchedAt ?? null,
            };
        });

        b.addCase(fetchTicketUserIdentity.pending, (s, a) => {
            const id = String(a.meta.arg.id ?? '').trim();
            const prev = s.userIdentityMetaById[id];
            s.userIdentityMetaById[id] = {
                loading: true,
                error: null,
                fetchedAt: prev?.fetchedAt ?? null,
            };
        });
        b.addCase(fetchTicketUserIdentity.fulfilled, (s, a) => {
            s.userIdentityById[a.payload.id] = a.payload.identity;
            s.userIdentityMetaById[a.payload.id] = {
                loading: false,
                error: null,
                fetchedAt: Date.now(),
            };
        });
        b.addCase(fetchTicketUserIdentity.rejected, (s, a) => {
            const id = String(a.meta.arg.id ?? '').trim();
            const prev = s.userIdentityMetaById[id];
            s.userIdentityMetaById[id] = {
                loading: false,
                error: a.error.message || 'Failed to load user identity',
                fetchedAt: prev?.fetchedAt ?? null,
            };
        });

        // create
        b.addCase(createTicket.pending, (s) => { s.creating = true; s.createError = null; });
        b.addCase(createTicket.fulfilled, (s) => { s.creating = false; });
        b.addCase(createTicket.rejected, (s, a) => { s.creating = false; s.createError = a.error.message || 'Create failed'; });

        // assign / status (optimistic-ish upserts)
        b.addCase(assignTicket.pending, (s) => { s.mutating = true; s.mutationError = null; });
        b.addCase(assignTicket.fulfilled, (s, a) => {
            s.mutating = false;
            const { id, agentId, teamId } = a.payload;
            ticketsAdapter.updateOne(s.tickets, { id, changes: { assignedAgentId: agentId, teamId } });
            if (s.ticketDetailsById[id]) {
                s.ticketDetailsById[id] = {
                    ...s.ticketDetailsById[id]!,
                    assignedAgentId: agentId,
                    teamId,
                };
            }
        });
        b.addCase(assignTicket.rejected, (s, a) => { s.mutating = false; s.mutationError = a.error.message || 'Assign failed'; });

        b.addCase(updateTicketStatus.pending, (s) => { s.mutating = true; s.mutationError = null; });
        b.addCase(updateTicketStatus.fulfilled, (s, a) => {
            s.mutating = false;
            ticketsAdapter.updateOne(s.tickets, { id: a.payload.id, changes: { status: a.payload.status } });
            if (s.ticketDetailsById[a.payload.id]) {
                s.ticketDetailsById[a.payload.id] = {
                    ...s.ticketDetailsById[a.payload.id]!,
                    status: a.payload.status,
                };
            }
        });
        b.addCase(updateTicketStatus.rejected, (s, a) => { s.mutating = false; s.mutationError = a.error.message || 'Status update failed'; });

        // add comment — let the live snapshot update the list; nothing else needed
        b.addCase(addTicketComment.pending, (s) => { s.mutating = true; s.mutationError = null; });
        b.addCase(addTicketComment.fulfilled, (s) => { s.mutating = false; });
        b.addCase(addTicketComment.rejected, (s, a) => { s.mutating = false; s.mutationError = a.error.message || 'Comment failed'; });
    }
});

export const ticketsReducer = slice.reducer;
export const {
    ticketUpserted,
    ticketPatched,
    ticketReplaced,
    ticketRemoved,
    commentsSnapshotReplaced,
    markLiveSubscribed,
    markLiveUnsubscribed
} = slice.actions;

/* ---------------- selectors ---------------- */
const ticketsSelectors = ticketsAdapter.getSelectors<RootState>(
    (s) => s.tickets.tickets
);

export const selectAllTickets = ticketsSelectors.selectAll;
export const selectTicketById = ticketsSelectors.selectById;
export const selectCachedTicketById = (ticketId: string) =>
    createSelector(
        (s: RootState) => ticketsSelectors.selectById(s, ticketId),
        (s: RootState) => s.tickets.ticketDetailsById[ticketId] ?? null,
        (listTicket, detailTicket) => {
            if (listTicket && detailTicket) return { ...listTicket, ...detailTicket };
            return detailTicket ?? listTicket ?? undefined;
        },
    );
export const selectTicketDetailMeta = (ticketId: string) =>
    (s: RootState) => s.tickets.ticketDetailMetaById[ticketId] ?? null;
export const selectTicketContextByKey = (key: string) =>
    (s: RootState) => s.tickets.ticketContextByKey[key] ?? null;
export const selectTicketContextMetaByKey = (key: string) =>
    (s: RootState) => s.tickets.ticketContextMetaByKey[key] ?? null;
export const selectTicketOrgAssigneesByKey = (key: string) =>
    (s: RootState) => s.tickets.orgAssigneesByKey[key] ?? EMPTY_ASSIGNEE_OPTIONS;
export const selectTicketOrgAssigneeMetaByKey = (key: string) =>
    (s: RootState) => s.tickets.orgAssigneeMetaByKey[key] ?? null;
export const selectTicketInternalTeamAssigneesByTeamId = (teamNodeId: string) =>
    (s: RootState) => s.tickets.internalTeamAssigneesByTeamId[teamNodeId] ?? EMPTY_ASSIGNEE_OPTIONS;
export const selectTicketInternalTeamAssigneeMetaByTeamId = (teamNodeId: string) =>
    (s: RootState) => s.tickets.internalTeamAssigneeMetaByTeamId[teamNodeId] ?? null;
export const selectTicketUserIdentityMap = (s: RootState) =>
    s.tickets.userIdentityById ?? EMPTY_USER_IDENTITY_MAP;
export const selectListLoading = (s: RootState) => s.tickets.listLoading;
export const selectLiveTicketIds = (s: RootState) => s.tickets.liveTicketIds;

// Common queue sorts
export const selectTicketsSortedBySLA = createSelector(
    selectAllTickets,
    (list) =>
        [...list].sort((a, b) => {
            const ax =
                a.plannedEndAt?.seconds ??
                a.plannedEndAt?._seconds ??
                a.slaResolutionDueAt?.seconds ??
                a.slaResolutionDueAt?._seconds ??
                Number.MAX_SAFE_INTEGER;
            const bx =
                b.plannedEndAt?.seconds ??
                b.plannedEndAt?._seconds ??
                b.slaResolutionDueAt?.seconds ??
                b.slaResolutionDueAt?._seconds ??
                Number.MAX_SAFE_INTEGER;
            return ax - bx;
        })
);

export const selectCommentsForTicket = (ticketId: string) =>
    (s: RootState) => s.tickets.commentsByTicket[ticketId]?.data ?? EMPTY_COMMENT_STATE;
const ticketCommentEntitySelectors = commentsAdapter.getSelectors();
export const selectCommentListForTicket = (ticketId: string) =>
    createSelector(
        selectCommentsForTicket(ticketId),
        (commentState) => ticketCommentEntitySelectors.selectAll(commentState),
    );
export const selectCommentBucketMeta = (ticketId: string) =>
    createSelector(
        (s: RootState) => s.tickets.commentsByTicket[ticketId],
        (bucket) => {
            if (!bucket) return EMPTY_COMMENT_BUCKET_META;
            return {
                loading: bucket.loading,
                error: bucket.error,
                fetchedAt: bucket.fetchedAt,
            };
        },
    );
